/**
 * 현재 UI 언어 훅 + 언어 적용기
 *
 * 🔴 사용자 언어의 SSOT 는 **DB(users.locale)** 다.
 * - 서버 권위 출처: GET /api/auth/me 의 locale (앱 부팅 시 적용 — {@link useMe})
 * - 변경(영속): PUT /api/account/settings (사용자 설정 화면의 '저장')
 * - 이 모듈은 그 값을 **런타임 UI에 반영**하기만 함. 브라우저 기억은 로그인 전 화면 전용(browserLang.ts)이고
 *   로그인하면 계정 언어로 덮어씀 — 로그인 후 화면이 브라우저 사본을 읽으면 서버값과 어긋남.
 *
 * - 현재 언어: i18next.language 기반 (react-i18next 리렌더 연동)
 * - applyLang: i18next 언어 전환 + dayjs locale 갱신 (메모리 한정 — 영속은 서버 API 책임)
 * - dayjs: UTC/timezone 플러그인 등록 (저장값 UTC → KST 표시 변환용)
 *
 * 시각은 항상 서버 응답값(UTC)을 dayjs로 변환해 표시. 클라이언트 로컬 시각 직접 생성 금지.
 */
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/ja";
import "dayjs/locale/ko";
import "dayjs/locale/zh-cn";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import i18next, { type SupportedLng } from "./index";

dayjs.extend(utc);
dayjs.extend(timezone);

/** i18next 언어 코드 → dayjs locale 코드 매핑 (zh → zh-cn) */
export const DAYJS_LOCALE: Record<SupportedLng, string> = {
  ko: "ko",
  en: "en",
  zh: "zh-cn",
  ja: "ja",
};

/** 언어 코드 → dayjs locale (미지원 코드는 ko). 임의 키 인덱싱 대신 Map 조회 */
const DAYJS_LOCALE_MAP = new Map<string, string>(Object.entries(DAYJS_LOCALE));

export function toDayjsLocale(lang: string): string {
  return DAYJS_LOCALE_MAP.get(lang) ?? "ko";
}

/** 언어 변경 시 dayjs locale 동기화 (모듈 로드 시 1회 + applyLang 시) */
function applyDayjsLocale(lang: string): void {
  const locale = toDayjsLocale(lang);
  dayjs.locale(locale);
}

/**
 * UI 언어 적용(i18next + dayjs) — React 밖(쿼리 훅 등)에서도 호출 가능한 순수 함수.
 * 영속화하지 않음. 저장은 서버 API(PUT /api/account/settings) 담당.
 */
export function applyLang(next: SupportedLng): void {
  void i18next.changeLanguage(next);
  applyDayjsLocale(next);
}

interface UseLangResult {
  /** 현재 UI 언어 코드 (ko/en/zh/ja) */
  lang: SupportedLng;
  /** UI 언어 적용 (i18next + dayjs — 영속화 없음) */
  applyLang: (next: SupportedLng) => void;
}

export function useLang(): UseLangResult {
  const { i18n } = useTranslation();

  const lang = ((i18n.language || "ko").split("-")[0] as SupportedLng) ?? "ko";

  const apply = useCallback((next: SupportedLng) => {
    applyLang(next);
  }, []);

  return { lang, applyLang: apply };
}

// 초기 로드 시 현재 i18next 언어(로그인 전 = 브라우저 언어 폴백)로 dayjs locale 동기화
applyDayjsLocale(i18next.language || "ko");
